import {
  CreateCampaignData,
  CreateCampaignDialogProps,
  CreatorCriterion,
} from "./types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Info, Loader2, Plus, Upload, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getTemplateBrief, getTemplateCreatorCriteria } from "./constants";
import { useCallback, useEffect, useRef, useState } from "react";
import { useConvexMutation, useConvexQuery } from "@convex-dev/react-query";

import { APPROVED_STRIPE_CONNECT_COUNTRIES } from "@/constants/approvedCountries";
import AiScrapingDialog from "./AiScrapingDialog";
import { Button } from "@/components/ui/button";
import { DEMO_IDS } from "@/mock/demo";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Switch } from "@/components/ui/switch";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { Textarea } from "@/components/ui/textarea";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { api } from "@cvx/_generated/api";
import { countries } from "countries-list";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
import { useAccount } from "@/state/useAccount";
import { useMutation } from "@tanstack/react-query";
import { useQuery } from "convex/react";
import { useToast } from "@/components/ui/use-toast";
import { useUploadFiles } from "@xixixao/uploadstuff/react";

// Helper function to check if rich text content is empty
const isRichTextEmpty = (content: string): boolean => {
  if (!content) return true;
  // Remove HTML tags and check if there's actual text content
  const textContent = content.replace(/<[^>]*>/g, "").trim();
  return textContent === "";
};

export default function CreateCampaignDialog({
  open,
  onOpenChange,
  appId,
  campaignToDuplicate,
}: CreateCampaignDialogProps) {
  const { demoModeEnabled } = useAccount();

  // Get current user to check if they're a team member
  const currentUser = useQuery(api.app.getCurrentUser, {});
  const isTeamMember = currentUser
    ? TEAM_USER_IDS.includes(currentUser._id)
    : false;

  // Check if we're in demo mode
  const isDemo =
    demoModeEnabled &&
    (appId as unknown as string) === (DEMO_IDS.appId as unknown as string);

  const [formData, setFormData] = useState<CreateCampaignData>({
    name: "",
    type: "CiR",
    isPublic: true,
    allowedChannels: ["tiktok", "youtube", "youtube-shorts", "instagram"],
    budget: undefined, // Add budget field
    allowCreatorsToPostDirectlyToSocials: true, // New field
    aiReviewRequired: false, // New field
    teamVideoReviewRequired: true, // Default to true for team review
    manualVideoReviewRequired: false,
    problemsSolved: "",
    customerTestimonials: [],
    eligibleCountries: APPROVED_STRIPE_CONNECT_COUNTRIES, // Default to all approved countries
  });

  // Separate state for campaign type to avoid conflicts
  const [campaignType, setCampaignType] = useState<
    "CiR" | "influencer" | "challenge"
  >("CiR");

  const [endDateInput, setEndDateInput] = useState("");
  const [showCpmCalculator, setShowCpmCalculator] = useState(false);
  const { toast } = useToast();
  const [cpmCalculator, setCpmCalculator] = useState({
    lifetimeRevenue: "100",
    cacLtvRatio: "0.33",
    viewsToDownload: "0.25",
    downloadToPaid: "8",
  });

  // Add video upload state
  const [uploadedVideos, setUploadedVideos] = useState<
    Array<{
      id: string;
      name: string;
      url?: string;
    }>
  >([]);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const videoUploadRef = useRef<HTMLInputElement>(null);

  // Add URL state
  const [campaignUrls, setCampaignUrls] = useState<
    Array<{
      id: string;
      url: string;
      title: string;
    }>
  >([]);

  // Inspiration Videos state
  const [inspirationVideos, setInspirationVideos] = useState<
    Array<{
      id: string;
      name: string;
      url?: string;
    }>
  >([]);
  const [isUploadingInspirationVideo, setIsUploadingInspirationVideo] =
    useState(false);
  const inspirationVideoUploadRef = useRef<HTMLInputElement>(null);

  // AI scraping state
  const [isAiScraping, setIsAiScraping] = useState(false);
  const [aiScrapingDialogOpen, setAiScrapingDialogOpen] = useState(false);
  const [aiScrapingDialogType, setAiScrapingDialogType] = useState<
    "usps" | "testimonials"
  >("usps");

  const generateUploadUrl = useConvexMutation(api.app.generateUploadUrl);

  const { startUpload } = useUploadFiles(generateUploadUrl, {
    onUploadComplete: async (uploaded) => {
      // Process all uploaded files
      const newVideos = uploaded.map((upload) => {
        const uploadResponse = upload.response as { storageId: string };
        return {
          id: uploadResponse.storageId,
          name: upload.name,
        };
      });

      setUploadedVideos((prev) => [...prev, ...newVideos]);

      setIsUploadingVideo(false);
      toast({
        title: `Video${uploaded.length > 1 ? "s" : ""} uploaded successfully`,
        description:
          uploaded.length > 1
            ? `${uploaded.length} videos have been uploaded`
            : `${uploaded[0].name} has been uploaded`,
      });
    },
    onUploadError: () => {
      setIsUploadingVideo(false);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your video(s)",
        variant: "destructive",
      });
    },
  });

  const handleVideoUpload = (files: File[]) => {
    if (files.length === 0) return;

    // File size limit: 100MB per file
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles.map((f) => f.name).join(", ");
      toast({
        title: "File size too large",
        description: `The following files exceed 100MB limit: ${oversizedFileNames}. Please compress your videos or upload them to YouTube/Google Drive and add the link instead.`,
        variant: "destructive",
      });
      return;
    }

    // Check total size of all files
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const MAX_TOTAL_SIZE = 200 * 1024 * 1024; // 200MB total

    if (totalSize > MAX_TOTAL_SIZE) {
      toast({
        title: "Total file size too large",
        description: `Total file size exceeds 200MB limit. Please upload fewer files or compress your videos first.`,
        variant: "destructive",
      });
      return;
    }

    setIsUploadingVideo(true);
    startUpload(files);
  };

  const removeVideo = (videoId: string) => {
    setUploadedVideos((prev) => prev.filter((video) => video.id !== videoId));
  };

  const addVideoUrl = () => {
    const url = prompt("Enter video URL:");
    if (url && url.trim()) {
      const videoId = Date.now().toString();
      setUploadedVideos((prev) => [
        ...prev,
        {
          id: videoId,
          name:
            url.includes("youtube.com") || url.includes("youtu.be")
              ? "YouTube Video"
              : url.includes("vimeo.com")
                ? "Vimeo Video"
                : "External Video",
          url: url.trim(),
        },
      ]);
    }
  };

  // Campaign URL management functions
  const addCampaignUrl = () => {
    const urlId = Date.now().toString();
    setCampaignUrls((prev) => [
      ...prev,
      {
        id: urlId,
        title: "",
        url: "",
      },
    ]);
  };

  const removeCampaignUrl = (urlId: string) => {
    setCampaignUrls((prev) => prev.filter((url) => url.id !== urlId));
  };

  const updateCampaignUrl = (
    urlId: string,
    field: "title" | "url",
    value: string,
  ) => {
    setCampaignUrls((prev) =>
      prev.map((url) => (url.id === urlId ? { ...url, [field]: value } : url)),
    );
  };

  const removeInspirationVideo = (videoId: string) => {
    setInspirationVideos((prev) =>
      prev.filter((video) => video.id !== videoId),
    );
  };

  const updateInspirationVideo = (videoId: string, value: string) => {
    setInspirationVideos((prev) =>
      prev.map((video) =>
        video.id === videoId ? { ...video, url: value, name: value } : video,
      ),
    );
  };

  const handleInspirationVideoUpload = (files: File[]) => {
    if (files.length === 0) return;

    // File size limit: 100MB per file
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles.map((f) => f.name).join(", ");
      toast({
        title: "File size too large",
        description: `The following files exceed 100MB limit: ${oversizedFileNames}. Please compress your videos or upload them to YouTube/TikTok/Instagram and add the link instead.`,
        variant: "destructive",
      });
      return;
    }

    // Check total size of all files
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const MAX_TOTAL_SIZE = 200 * 1024 * 1024; // 200MB total

    if (totalSize > MAX_TOTAL_SIZE) {
      toast({
        title: "Total file size too large",
        description: `Total file size exceeds 200MB limit. Please upload fewer files or compress your videos first.`,
        variant: "destructive",
      });
      return;
    }

    setIsUploadingInspirationVideo(true);
    startUploadInspirationVideos(files);
  };

  const { startUpload: startUploadInspirationVideos } = useUploadFiles(
    generateUploadUrl,
    {
      onUploadComplete: async (uploaded) => {
        // Process all uploaded files
        const newVideos = uploaded.map((upload) => {
          const uploadResponse = upload.response as { storageId: string };
          return {
            id: uploadResponse.storageId,
            name: upload.name,
            url: undefined,
          };
        });

        setInspirationVideos((prev) => [...prev, ...newVideos]);
        setIsUploadingInspirationVideo(false);

        toast({
          title: `Inspiration video${uploaded.length > 1 ? "s" : ""} uploaded`,
          description:
            uploaded.length > 1
              ? `${uploaded.length} videos have been uploaded successfully`
              : `${uploaded[0].name} has been uploaded successfully`,
        });
      },
      onUploadError: () => {
        setIsUploadingInspirationVideo(false);
        toast({
          title: "Upload failed",
          description: "There was an error uploading your video(s)",
          variant: "destructive",
        });
      },
    },
  );

  const addInspirationVideoUrl = () => {
    const url = prompt("Enter inspiration video URL:");
    if (url && url.trim()) {
      const videoId = Date.now().toString();
      setInspirationVideos((prev) => [
        ...prev,
        {
          id: videoId,
          name:
            url.includes("youtube.com") || url.includes("youtu.be")
              ? "YouTube Video"
              : url.includes("vimeo.com")
                ? "Vimeo Video"
                : url.includes("tiktok.com")
                  ? "TikTok Video"
                  : url.includes("instagram.com")
                    ? "Instagram Video"
                    : "External Video",
          url: url.trim(),
        },
      ]);
    }
  };

  const calculateRecommendedCpm = () => {
    const { lifetimeRevenue, cacLtvRatio, viewsToDownload, downloadToPaid } =
      cpmCalculator;

    const ltv = parseFloat(lifetimeRevenue) || 0;
    const cacRatio = parseFloat(cacLtvRatio) || 0;
    const viewsConversion = parseFloat(viewsToDownload) || 0;
    const downloadConversion = parseFloat(downloadToPaid) || 0;

    return (
      1000 *
      (viewsConversion / 100) *
      (downloadConversion / 100) *
      (ltv * cacRatio)
    );
  };

  const { mutateAsync: createCampaign, isPending } = useMutation({
    mutationFn: useConvexMutation(api.campaigns.createUpdateCampaign),
  });

  const scrapeWebsite = useConvexMutation(api.campaigns.scrapeWebsiteForApp);
  const processFile = useConvexMutation(api.campaigns.processFileForApp);
  const appAdditionalInfo = useConvexQuery(
    api.campaigns.getAppAdditionalInfo,
    isDemo ? "skip" : { appId },
  );

  // Fetch brand/app to display brand-specific team name
  const appInfo = useConvexQuery(api.apps.getApp, { appId });
  const brandName = appInfo?.name || "Brand";

  const updateAppAdditionalInfo = useConvexMutation(
    api.campaigns.updateAppAdditionalInfoFromForm,
  );

  // Separate debounced auto-save for USPs and testimonials
  const uspsDebounceTimeoutRef = useRef<NodeJS.Timeout>();
  const testimonialsDebounceTimeoutRef = useRef<NodeJS.Timeout>();
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  const debouncedSaveUSPs = useCallback(
    async (problemsSolved: string | undefined) => {
      if (uspsDebounceTimeoutRef.current) {
        clearTimeout(uspsDebounceTimeoutRef.current);
      }

      const currentAppId = appId; // Capture current appId at the time of calling
      uspsDebounceTimeoutRef.current = setTimeout(async () => {
        try {
          // Safety check: only save if appId hasn't changed
          if (currentAppId !== appId) {
            console.warn(
              "USPs auto-save cancelled: appId changed during debounce",
            );
            return;
          }

          setIsAutoSaving(true);
          await updateAppAdditionalInfo({
            appId: currentAppId,
            problemsSolved: problemsSolved || undefined,
            // Don't include testimonials - only save USPs
          });
        } catch (error) {
          console.warn("USPs auto-save failed:", error);
        } finally {
          setIsAutoSaving(false);
        }
      }, 2000); // 2 second debounce
    },
    [updateAppAdditionalInfo, appId],
  );

  const debouncedSaveTestimonials = useCallback(
    async (testimonials: typeof formData.customerTestimonials) => {
      if (testimonialsDebounceTimeoutRef.current) {
        clearTimeout(testimonialsDebounceTimeoutRef.current);
      }

      const currentAppId = appId; // Capture current appId at the time of calling
      testimonialsDebounceTimeoutRef.current = setTimeout(async () => {
        try {
          // Safety check: only save if appId hasn't changed
          if (currentAppId !== appId) {
            console.warn(
              "Testimonials auto-save cancelled: appId changed during debounce",
            );
            return;
          }

          setIsAutoSaving(true);
          await updateAppAdditionalInfo({
            appId: currentAppId,
            // Don't include problemsSolved - only save testimonials
            customerTestimonials:
              testimonials && testimonials.length > 0
                ? testimonials
                : undefined,
          });
        } catch (error) {
          console.warn("Testimonials auto-save failed:", error);
        } finally {
          setIsAutoSaving(false);
        }
      }, 2000); // 2 second debounce
    },
    [updateAppAdditionalInfo, appId],
  );

  // Auto-save USPs when problemsSolved changes
  useEffect(() => {
    if (formData.problemsSolved !== "") {
      debouncedSaveUSPs(formData.problemsSolved);
    }
  }, [formData.problemsSolved, debouncedSaveUSPs]);

  // Auto-save testimonials when customerTestimonials change
  useEffect(() => {
    if (
      formData.customerTestimonials &&
      formData.customerTestimonials.length > 0
    ) {
      debouncedSaveTestimonials(formData.customerTestimonials);
    }
  }, [formData.customerTestimonials, debouncedSaveTestimonials]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (uspsDebounceTimeoutRef.current) {
        clearTimeout(uspsDebounceTimeoutRef.current);
      }
      if (testimonialsDebounceTimeoutRef.current) {
        clearTimeout(testimonialsDebounceTimeoutRef.current);
      }
    };
  }, []);

  // Track the last known appAdditionalInfo and appId to detect changes
  const lastAdditionalInfoRef = useRef<Array<{
    title: string;
    description: string;
    type: "usps" | "testimonial";
  }> | null>(null);
  const lastAppIdRef = useRef<string | null>(null);

  // Reset form when appId changes (switching apps) or dialog opens
  useEffect(() => {
    const currentAppId = appId as unknown as string;

    // If appId has changed, reset the form data
    if (
      lastAppIdRef.current !== null &&
      lastAppIdRef.current !== currentAppId
    ) {
      // CRITICAL: Clear any pending auto-save timeouts to prevent saving old data to new app
      if (uspsDebounceTimeoutRef.current) {
        clearTimeout(uspsDebounceTimeoutRef.current);
        uspsDebounceTimeoutRef.current = undefined;
      }
      if (testimonialsDebounceTimeoutRef.current) {
        clearTimeout(testimonialsDebounceTimeoutRef.current);
        testimonialsDebounceTimeoutRef.current = undefined;
      }

      setFormData({
        name: "",
        type: "CiR",
        isPublic: true,
        allowedChannels: ["tiktok", "youtube", "youtube-shorts", "instagram"],
        budget: undefined,
        allowCreatorsToPostDirectlyToSocials: true,
        aiReviewRequired: false,
        teamVideoReviewRequired: false,
        manualVideoReviewRequired: false,
        problemsSolved: "",
        customerTestimonials: [],
        eligibleCountries: APPROVED_STRIPE_CONNECT_COUNTRIES,
      });
      setCampaignType("CiR");
      setEndDateInput("");
      setShowCpmCalculator(false);
      setCpmCalculator({
        lifetimeRevenue: "100",
        cacLtvRatio: "0.33",
        viewsToDownload: "0.25",
        downloadToPaid: "8",
      });
      setUploadedVideos([]);
      setCampaignUrls([]);
      setInspirationVideos([]);

      // Clear the refs to ensure fresh data loading
      lastAdditionalInfoRef.current = null;
    }

    // Update the ref to track current appId
    lastAppIdRef.current = currentAppId;
  }, [appId]);

  // Reset form when dialog opens to ensure clean state
  useEffect(() => {
    if (open) {
      // Clear the additional info fields when dialog opens
      setFormData((prev) => ({
        ...prev,
        problemsSolved: "",
        customerTestimonials: [],
      }));

      // Clear the refs to ensure fresh data loading
      lastAdditionalInfoRef.current = null;
    }
  }, [open]);

  // Pre-populate form when duplicating a campaign
  useEffect(() => {
    if (campaignToDuplicate && open) {
      // Convert additionalInfo to problemsSolved and customerTestimonials
      // First try campaign's additionalInfo, then fall back to app's additionalInfo
      let problemsSolved = "";
      let customerTestimonials: Array<{
        testimonial: string;
        customerName: string;
      }> = [];

      const additionalInfoSource =
        campaignToDuplicate.additionalInfo || appAdditionalInfo || [];

      if (additionalInfoSource.length > 0) {
        const uspsItems = additionalInfoSource.filter(
          (item) => item.type === "usps",
        );
        if (uspsItems.length > 0) {
          problemsSolved = uspsItems
            .map((item) => {
              if (
                item.description.includes("<") &&
                item.description.includes(">")
              ) {
                return item.description;
              } else {
                return `<h3>${item.title}</h3><p>${item.description}</p>`;
              }
            })
            .join("");
        }

        const testimonialItems = additionalInfoSource.filter(
          (item) => item.type === "testimonial",
        );
        if (testimonialItems.length > 0) {
          customerTestimonials = testimonialItems.map((item) => ({
            testimonial: item.description,
            customerName: item.title,
          }));
        }
      }

      // Convert videos to uploadedVideos format
      const videos = (campaignToDuplicate.videos || []).map((video, index) => {
        if (typeof video === "string" && video.startsWith("http")) {
          return {
            id: Date.now().toString() + index,
            name:
              video.includes("youtube.com") || video.includes("youtu.be")
                ? "YouTube Video"
                : video.includes("vimeo.com")
                  ? "Vimeo Video"
                  : "External Video",
            url: video,
          };
        } else {
          return {
            id: video as string,
            name: `Campaign Video ${index + 1}`,
          };
        }
      });

      // Convert inspirationVideos to inspirationVideos format
      const inspirationVids = (campaignToDuplicate.inspirationVideos || []).map(
        (video, index) => {
          if (typeof video === "string" && video.startsWith("http")) {
            return {
              id: Date.now().toString() + index,
              name:
                video.includes("youtube.com") || video.includes("youtu.be")
                  ? "YouTube Video"
                  : video.includes("vimeo.com")
                    ? "Vimeo Video"
                    : video.includes("tiktok.com")
                      ? "TikTok Video"
                      : video.includes("instagram.com")
                        ? "Instagram Video"
                        : "External Video",
              url: video,
            };
          } else {
            return {
              id: video as string,
              name: `Inspiration Video ${index + 1}`,
            };
          }
        },
      );

      // Convert URLs to campaignUrls format
      const urls = (campaignToDuplicate.urls || []).map((url, index) => ({
        id: Date.now().toString() + index,
        title: url.title,
        url: url.url,
      }));

      // Convert endDate timestamp to date string
      const endDateStr = campaignToDuplicate.endDate
        ? new Date(campaignToDuplicate.endDate).toISOString().split("T")[0]
        : "";

      // Pre-populate all form fields
      setFormData({
        name: `${campaignToDuplicate.name} (Copy)`,
        type: campaignToDuplicate.type,
        isPublic: campaignToDuplicate.isPublic,
        applicationNotRequired: campaignToDuplicate.applicationNotRequired,
        allowedChannels: (campaignToDuplicate.allowedChannels as (
          | "tiktok"
          | "youtube"
          | "youtube-shorts"
          | "instagram"
        )[]) || ["tiktok", "youtube", "youtube-shorts", "instagram"],
        budget: campaignToDuplicate.prizePool, // Convert prizePool to budget
        allowCreatorsToPostDirectlyToSocials:
          campaignToDuplicate.allowCreatorsToPostDirectlyToSocials ?? true,
        aiReviewRequired: campaignToDuplicate.aiReviewRequired ?? false,
        teamVideoReviewRequired:
          campaignToDuplicate.teamVideoReviewRequired ?? true,
        manualVideoReviewRequired:
          campaignToDuplicate.manualVideoReviewRequired ?? false,
        validationRequirements:
          campaignToDuplicate.validationRequirements || "",
        creatorBrief: campaignToDuplicate.creatorBrief || "",
        creatorCriteria: campaignToDuplicate.creatorCriteria || undefined,
        budgetCpmMin: campaignToDuplicate.budgetCpmMin,
        budgetCpmMax: campaignToDuplicate.budgetCpmMax,
        budgetFlatFeeMin: campaignToDuplicate.budgetFlatFeeMin,
        budgetFlatFeeMax: campaignToDuplicate.budgetFlatFeeMax,
        predefinedCpm: campaignToDuplicate.predefinedCpm,
        problemsSolved,
        customerTestimonials,
        eligibleCountries:
          campaignToDuplicate.eligibleCountries ||
          APPROVED_STRIPE_CONNECT_COUNTRIES,
      });

      setCampaignType(campaignToDuplicate.type);
      setEndDateInput(endDateStr);
      setUploadedVideos(videos);
      setCampaignUrls(urls);
      setInspirationVideos(inspirationVids);
    } else if (!campaignToDuplicate && open) {
      // Reset form when opening without a campaign to duplicate
      setFormData({
        name: "",
        type: "CiR",
        isPublic: true,
        allowedChannels: ["tiktok", "youtube", "youtube-shorts", "instagram"],
        budget: undefined,
        allowCreatorsToPostDirectlyToSocials: true,
        aiReviewRequired: false,
        teamVideoReviewRequired: true,
        manualVideoReviewRequired: false,
        problemsSolved: "",
        customerTestimonials: [],
        eligibleCountries: APPROVED_STRIPE_CONNECT_COUNTRIES,
      });
      setCampaignType("CiR");
      setEndDateInput("");
      setUploadedVideos([]);
      setCampaignUrls([]);
      setInspirationVideos([]);
    }
  }, [campaignToDuplicate, open, appAdditionalInfo]);

  // Auto-populate form fields with app additionalInfo
  // Skip this if we're duplicating a campaign (to preserve duplicated values)
  useEffect(() => {
    if (campaignToDuplicate) {
      // Skip auto-population when duplicating - values are already set
      return;
    }

    if (appAdditionalInfo && appAdditionalInfo.length > 0) {
      // Check if appAdditionalInfo has actually changed (not just a re-render)
      const hasChanged =
        JSON.stringify(lastAdditionalInfoRef.current) !==
        JSON.stringify(appAdditionalInfo);

      // Find USPs and populate the problemsSolved field
      const uspsItems = appAdditionalInfo.filter(
        (item) => item.type === "usps",
      );

      // Update USPs if: no existing content OR appAdditionalInfo changed
      if (uspsItems.length > 0 && (!formData.problemsSolved || hasChanged)) {
        const uspsContent = uspsItems
          .map((item) => {
            // Check if description already contains HTML formatting
            if (
              item.description.includes("<") &&
              item.description.includes(">")
            ) {
              return item.description; // Already formatted as HTML
            } else {
              // Fallback to simple HTML formatting if not already formatted
              return `<h3>${item.title}</h3><p>${item.description}</p>`;
            }
          })
          .join("");

        // Only update if the content is actually different
        if (uspsContent !== formData.problemsSolved) {
          setFormData((prev) => ({ ...prev, problemsSolved: uspsContent }));
        }
      }

      // Find testimonials and populate the customerTestimonials field
      const testimonialItems = appAdditionalInfo.filter(
        (item) => item.type === "testimonial",
      );

      // Update testimonials if: no existing content OR appAdditionalInfo changed
      if (
        testimonialItems.length > 0 &&
        (!formData.customerTestimonials ||
          formData.customerTestimonials.length === 0 ||
          hasChanged)
      ) {
        const testimonials = testimonialItems.map((item) => ({
          testimonial: item.description,
          customerName: item.title,
        }));

        // Only update if the testimonials are actually different
        const testimonialsChanged =
          JSON.stringify(testimonials) !==
          JSON.stringify(formData.customerTestimonials);
        if (testimonialsChanged) {
          setFormData((prev) => ({
            ...prev,
            customerTestimonials: testimonials,
          }));
        }
      }

      // Update the ref to track current data
      lastAdditionalInfoRef.current = appAdditionalInfo;
    } else if (appAdditionalInfo && appAdditionalInfo.length === 0) {
      // If appAdditionalInfo is empty array, clear the form fields
      const hasChanged =
        JSON.stringify(lastAdditionalInfoRef.current) !==
        JSON.stringify(appAdditionalInfo);

      if (hasChanged) {
        setFormData((prev) => ({
          ...prev,
          problemsSolved: "",
          customerTestimonials: [],
        }));
        lastAdditionalInfoRef.current = appAdditionalInfo;
      }
    }
  }, [
    appAdditionalInfo,
    formData.problemsSolved,
    formData.customerTestimonials,
    campaignToDuplicate,
  ]);

  // Helper function to determine if applications are required
  const requiresApplications = () => {
    if (campaignType === "challenge") {
      return formData.isPublic && !formData.applicationNotRequired;
    }
    return formData.isPublic; // For CiR and influencer campaigns, public means applications are accepted
  };

  const addCriterion = () => {
    const newCriterion: CreatorCriterion = {
      title: "",
      description: "",
      weight: undefined,
    };
    setFormData({
      ...formData,
      creatorCriteria: [...(formData.creatorCriteria || []), newCriterion],
    });
  };

  const removeCriterion = (index: number) => {
    const updatedCriteria = formData.creatorCriteria?.filter(
      (_, i) => i !== index,
    );
    setFormData({
      ...formData,
      creatorCriteria: updatedCriteria,
    });
  };

  const updateCriterion = (
    index: number,
    field: keyof CreatorCriterion,
    value: string | number,
  ) => {
    const updatedCriteria = formData.creatorCriteria?.map((criterion, i) =>
      i === index ? { ...criterion, [field]: value } : criterion,
    );
    setFormData({
      ...formData,
      creatorCriteria: updatedCriteria,
    });
  };

  const useTemplateCriteria = () => {
    setFormData({
      ...formData,
      creatorCriteria: getTemplateCreatorCriteria(),
    });
  };

  // Testimonial management functions
  const addTestimonial = () => {
    const updatedTestimonials = [
      ...(formData.customerTestimonials || []),
      { testimonial: "", customerName: "" },
    ];
    setFormData({
      ...formData,
      customerTestimonials: updatedTestimonials,
    });

    // Trigger immediate auto-save when testimonial is added
    debouncedSaveTestimonials(updatedTestimonials);
  };

  const removeTestimonial = (index: number) => {
    const updatedTestimonials = formData.customerTestimonials?.filter(
      (_, i) => i !== index,
    );
    setFormData({
      ...formData,
      customerTestimonials: updatedTestimonials,
    });

    // Trigger immediate auto-save when testimonial is removed
    debouncedSaveTestimonials(updatedTestimonials);
  };

  const updateTestimonial = (
    index: number,
    field: "testimonial" | "customerName",
    value: string,
  ) => {
    const updatedTestimonials = formData.customerTestimonials?.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    setFormData({
      ...formData,
      customerTestimonials: updatedTestimonials,
    });
  };

  // AI scraping function
  const handleAiScraping = async (data: { url?: string; file?: File }) => {
    if (!appId) {
      toast({
        title: "Error",
        description: "App ID is required for processing.",
        variant: "destructive",
      });
      return;
    }

    setIsAiScraping(true);
    try {
      if (data.url) {
        // Handle URL scraping
        await scrapeWebsite({
          websiteUrl: data.url.trim(),
          type:
            aiScrapingDialogType === "testimonials" ? "testimonial" : "usps",
          appId,
        });

        toast({
          title: "AI Analysis Started",
          description: `AI is analyzing your website for ${aiScrapingDialogType}. This may take a few minutes.`,
        });
      } else if (data.file) {
        // Handle file upload and processing
        const uploadedFiles = await startUpload([data.file]);
        if (uploadedFiles.length > 0) {
          const uploadResponse = uploadedFiles[0].response as {
            storageId: string;
          };

          await processFile({
            fileId: uploadResponse.storageId as Id<"_storage">,
            type:
              aiScrapingDialogType === "testimonials" ? "testimonial" : "usps",
            appId,
          });

          toast({
            title: "AI Analysis Started",
            description: `AI is analyzing your document for ${aiScrapingDialogType}. This may take a few minutes.`,
          });
        }
      }

      setAiScrapingDialogOpen(false);
    } catch (error) {
      toast({
        title: "Processing Failed",
        description: "Failed to start AI analysis. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAiScraping(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      type: "CiR",
      isPublic: true,
      allowedChannels: ["tiktok", "youtube", "youtube-shorts", "instagram"],
      budget: undefined, // Reset budget field
      manualVideoReviewRequired: false,
      problemsSolved: "",
      customerTestimonials: [],
      eligibleCountries: APPROVED_STRIPE_CONNECT_COUNTRIES,
    });
    setCampaignType("CiR");
    setEndDateInput("");
    setShowCpmCalculator(false);
    setCpmCalculator({
      lifetimeRevenue: "100",
      cacLtvRatio: "0.33",
      viewsToDownload: "0.25",
      downloadToPaid: "8",
    });
    setUploadedVideos([]);
    setCampaignUrls([]);
    setInspirationVideos([]);
    setIsUploadingVideo(false);
    setIsUploadingInspirationVideo(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Campaign name is required.",
        variant: "destructive",
      });
      return;
    }

    // Challenge campaign specific validations
    if (campaignType === "challenge") {
      if (!formData.predefinedCpm || formData.predefinedCpm <= 0) {
        toast({
          title: "Error",
          description: "CPM reward is required for challenge campaigns.",
          variant: "destructive",
        });
        return;
      }

      if (
        !formData.validationRequirements ||
        isRichTextEmpty(formData.validationRequirements)
      ) {
        toast({
          title: "Error",
          description:
            "Post validation requirements are required for challenge campaigns.",
          variant: "destructive",
        });
        return;
      }
    }

    // Creator criteria validation
    if (requiresApplications() && formData.creatorCriteria) {
      const hasEmptyFields = formData.creatorCriteria.some(
        (criterion) => !criterion.title.trim() || !criterion.description.trim(),
      );

      if (hasEmptyFields) {
        toast({
          title: "Error",
          description:
            "All creator criteria must have both title and description.",
          variant: "destructive",
        });
        return;
      }
    }

    // Validate budget if provided
    if (formData.budget !== undefined && formData.budget <= 0) {
      toast({
        title: "Error",
        description: "Campaign budget must be greater than 0.",
        variant: "destructive",
      });
      return;
    }

    // Validate budget ranges if provided
    if (
      formData.budgetCpmMin !== undefined &&
      formData.budgetCpmMax !== undefined &&
      formData.budgetCpmMin > formData.budgetCpmMax
    ) {
      toast({
        title: "Error",
        description:
          "Minimum CPM budget cannot be higher than maximum CPM budget.",
        variant: "destructive",
      });
      return;
    }

    if (
      formData.budgetFlatFeeMin !== undefined &&
      formData.budgetFlatFeeMax !== undefined &&
      formData.budgetFlatFeeMin > formData.budgetFlatFeeMax
    ) {
      toast({
        title: "Error",
        description:
          "Minimum flat fee budget cannot be higher than maximum flat fee budget.",
        variant: "destructive",
      });
      return;
    }

    try {
      const campaignData = {
        appId,
        name: formData.name.trim(),
        type: campaignType,
        isPublic: formData.isPublic,
        applicationNotRequired: formData.applicationNotRequired,
        allowCreatorsToPostDirectlyToSocials:
          formData.allowCreatorsToPostDirectlyToSocials,
        aiReviewRequired: formData.aiReviewRequired,
        teamVideoReviewRequired: formData.teamVideoReviewRequired,
        manualVideoReviewRequired: formData.manualVideoReviewRequired,
        allowedChannels: formData.allowedChannels,
        validationRequirements:
          formData.validationRequirements &&
          !isRichTextEmpty(formData.validationRequirements)
            ? formData.validationRequirements
            : undefined,
        creatorBrief: formData.creatorBrief?.trim() || undefined,
        creatorCriteria: formData.creatorCriteria || undefined,
        endDate: endDateInput ? new Date(endDateInput).getTime() : undefined,
        prizePool: formData.budget, // Map budget to prizePool
        budgetCpmMin: formData.budgetCpmMin,
        budgetCpmMax: formData.budgetCpmMax,
        budgetFlatFeeMin: formData.budgetFlatFeeMin,
        budgetFlatFeeMax: formData.budgetFlatFeeMax,
        predefinedCpm: formData.predefinedCpm,
        videos:
          uploadedVideos.length > 0
            ? uploadedVideos.map((video) => video.url || video.id)
            : undefined,
        urls:
          campaignUrls.length > 0
            ? campaignUrls.map((url) => ({
                url: url.url,
                title: url.title,
              }))
            : undefined,
        inspirationVideos:
          inspirationVideos.length > 0
            ? inspirationVideos.map((video) => video.url || video.id)
            : undefined,
        eligibleCountries: formData.eligibleCountries,
      };

      await createCampaign(campaignData);

      // Save additional info to the app (not the campaign)
      const shouldUpdateAppInfo =
        (formData.problemsSolved &&
          !isRichTextEmpty(formData.problemsSolved)) ||
        (formData.customerTestimonials &&
          formData.customerTestimonials.length > 0 &&
          formData.customerTestimonials.some(
            (t) => !isRichTextEmpty(t.testimonial) && t.customerName.trim(),
          ));

      if (shouldUpdateAppInfo) {
        await updateAppAdditionalInfo({
          appId,
          problemsSolved:
            formData.problemsSolved && !isRichTextEmpty(formData.problemsSolved)
              ? formData.problemsSolved
              : undefined,
          customerTestimonials:
            formData.customerTestimonials &&
            formData.customerTestimonials.length > 0
              ? formData.customerTestimonials.filter(
                  (t) =>
                    !isRichTextEmpty(t.testimonial) && t.customerName.trim(),
                )
              : undefined,
        });
      }

      toast({
        title: "Campaign created!",
        description: `${formData.name} has been created successfully.`,
      });

      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create campaign. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {campaignToDuplicate ? "Duplicate Campaign" : "Create New Campaign"}
          </DialogTitle>
          <DialogDescription>
            {campaignToDuplicate
              ? "Duplicate an existing campaign with all its settings. You can modify any fields before creating."
              : "Set up a new marketing campaign to work with creators and track performance."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Campaign Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              Campaign Name *
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="Enter campaign name"
              required
            />
            <p className="text-xs text-muted-foreground">
              Choose a descriptive name that helps you identify this campaign.
              This will be seen by creators and be publicly visible.
            </p>
          </div>

          {/* Marketing Videos Section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Marketing Videos (Optional)
              </Label>
              <p className="text-xs text-muted-foreground">
                Upload videos to help creators understand your app better - app
                demos, founder introductions, explainer videos, etc.
                <strong>File limit: 100MB per video, 200MB total.</strong>
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-6">
                {isUploadingVideo ? (
                  <>
                    <Loader2 className="mb-2 h-8 w-8 animate-spin text-primary/60" />
                    <p className="mb-1 text-sm font-medium">
                      Uploading video...
                    </p>
                    <p className="text-xs text-primary/60">
                      Please wait while we process your video
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="mb-2 h-8 w-8 text-primary/60" />
                    <p className="mb-1 text-sm font-medium">
                      Upload marketing videos
                    </p>
                    <p className="mb-4 text-xs text-primary/60">
                      Drag and drop or click to select video files (multiple
                      files supported). Max 100MB per file, 200MB total.
                    </p>
                    <div className="flex gap-2">
                      <input
                        ref={videoUploadRef}
                        type="file"
                        accept="video/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          handleVideoUpload(files);
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => videoUploadRef.current?.click()}
                        disabled={isUploadingVideo}
                      >
                        Upload Videos
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addVideoUrl}
                        disabled={isUploadingVideo}
                      >
                        Add Video URL
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Display uploaded videos */}
              {uploadedVideos.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium">Marketing Videos:</h5>
                  {uploadedVideos.map((video) => (
                    <div
                      key={video.id}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900/20 rounded flex items-center justify-center">
                          <span className="text-xs text-blue-600 dark:text-blue-400">
                            📹
                          </span>
                        </div>
                        <div>
                          <span className="text-sm font-medium">
                            {video.name}
                          </span>
                          {video.url && (
                            <p className="text-xs text-muted-foreground">
                              External URL
                            </p>
                          )}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVideo(video.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">Marketing Video Tips:</p>
                <ul className="space-y-0.5">
                  <li>
                    • App demo videos help creators understand your product
                  </li>
                  <li>• Founder introduction videos add personal touch</li>
                  <li>• Explainer videos show key features and benefits</li>
                  <li>
                    • Keep videos concise and engaging (2-5 minutes ideal)
                  </li>
                  <li>
                    • <strong>Large files?</strong> Upload to YouTube/Google
                    Drive and add as a link instead
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Campaign URLs Section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Useful Links (Optional)
              </Label>
              <p className="text-xs text-muted-foreground">
                Add helpful links that creators can access - TestFlight links,
                demo pages, documentation, link to drive with b-roll footage,
                etc.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addCampaignUrl}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Link
                </Button>
              </div>

              {/* Display campaign URLs */}
              {campaignUrls.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium">Useful Links:</h5>
                  {campaignUrls.map((urlItem) => (
                    <div
                      key={urlItem.id}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <div className="h-8 w-8 bg-green-100 dark:bg-green-900/20 rounded flex items-center justify-center">
                          <span className="text-xs text-green-600 dark:text-green-400">
                            🔗
                          </span>
                        </div>
                        <div className="flex-1 space-y-1">
                          <Input
                            value={urlItem.title}
                            onChange={(e) =>
                              updateCampaignUrl(
                                urlItem.id,
                                "title",
                                e.target.value,
                              )
                            }
                            placeholder="Link title"
                            className="text-sm font-medium"
                          />
                          <Input
                            value={urlItem.url}
                            onChange={(e) =>
                              updateCampaignUrl(
                                urlItem.id,
                                "url",
                                e.target.value,
                              )
                            }
                            placeholder="https://..."
                            className="text-xs"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCampaignUrl(urlItem.id)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Past Successful UGC Videos Section */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Past Successful UGC Videos (Optional)
              </Label>
              <p className="text-xs text-muted-foreground">
                Upload videos or add links to past successful user-generated
                content videos to inspire creators. These will be shown to
                creators as examples of great content.{" "}
                <strong>File limit: 100MB per video, 200MB total.</strong>
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-6">
                {isUploadingInspirationVideo ? (
                  <>
                    <Loader2 className="mb-2 h-8 w-8 animate-spin text-primary/60" />
                    <p className="mb-1 text-sm font-medium">
                      Uploading video...
                    </p>
                    <p className="text-xs text-primary/60">
                      Please wait while we process your video
                    </p>
                  </>
                ) : (
                  <>
                    <p className="mb-1 text-sm font-medium">
                      Add inspiration videos
                    </p>
                    <p className="mb-4 text-xs text-primary/60">
                      Upload video files or add external links (YouTube, TikTok,
                      Instagram). Max 100MB per file, 200MB total.
                    </p>
                    <div className="flex gap-2">
                      <input
                        ref={inspirationVideoUploadRef}
                        type="file"
                        accept="video/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          handleInspirationVideoUpload(files);
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          inspirationVideoUploadRef.current?.click()
                        }
                        disabled={isUploadingInspirationVideo}
                      >
                        Upload Videos
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addInspirationVideoUrl}
                        disabled={isUploadingInspirationVideo}
                      >
                        Add Video URL
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Display inspiration videos */}
              {inspirationVideos.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium">Inspiration Videos:</h5>
                  {inspirationVideos.map((videoItem) => (
                    <div
                      key={videoItem.id}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                    >
                      {videoItem.url ? (
                        // URL-based video - show editable input
                        <>
                          <div className="flex items-center gap-2 flex-1">
                            <div className="h-8 w-8 bg-purple-100 dark:bg-purple-900/20 rounded flex items-center justify-center">
                              <span className="text-xs text-purple-600 dark:text-purple-400">
                                🎬
                              </span>
                            </div>
                            <Input
                              value={videoItem.url}
                              onChange={(e) =>
                                updateInspirationVideo(
                                  videoItem.id,
                                  e.target.value,
                                )
                              }
                              placeholder="https://youtube.com/... or https://tiktok.com/... or https://instagram.com/..."
                              className="text-sm"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeInspirationVideo(videoItem.id)}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        // Uploaded video - show file name
                        <>
                          <div className="flex items-center gap-2 flex-1">
                            <div className="h-8 w-8 bg-purple-100 dark:bg-purple-900/20 rounded flex items-center justify-center">
                              <span className="text-xs text-purple-600 dark:text-purple-400">
                                🎬
                              </span>
                            </div>
                            <div className="flex-1">
                              <span className="text-sm font-medium">
                                {videoItem.name}
                              </span>
                              <p className="text-xs text-purple-600 dark:text-purple-400">
                                Uploaded file
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeInspirationVideo(videoItem.id)}
                            className="text-red-500 hover:text-red-700 ml-2"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Campaign Type */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Campaign Type *</Label>
            <Select
              value={campaignType}
              onValueChange={(value: "CiR" | "influencer" | "challenge") => {
                setCampaignType(value);
                setFormData((prev) => ({ ...prev, type: value }));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select campaign type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CiR">Creator in Residence (CiR)</SelectItem>
                <SelectItem value="influencer">Influencer Campaign</SelectItem>
                <SelectItem value="challenge">Challenge Campaign</SelectItem>
              </SelectContent>
            </Select>
            <div className="space-y-3 text-xs text-muted-foreground">
              <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="font-semibold text-blue-800 dark:text-blue-300 mb-2">
                  Creator in Residence (CiR):
                </p>
                <ul className="space-y-1 text-blue-700 dark:text-blue-400">
                  <li>
                    • Long-term partnership with creators who become brand
                    ambassadors
                  </li>
                  <li>
                    • <strong>No review process required</strong> - creators can
                    auto-track their posts using our platform
                  </li>
                  <li>
                    • Ideal for ongoing content creation and deeper brand
                    integration
                  </li>
                  <li>• Streamlined workflow for established partnerships</li>
                </ul>
              </div>

              <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="font-semibold text-purple-800 dark:text-purple-300 mb-2">
                  Influencer Campaign:
                </p>
                <ul className="space-y-1 text-purple-700 dark:text-purple-400">
                  <li>
                    • Short-term or one-off collaborations for specific
                    promotions
                  </li>
                  <li>
                    • <strong>Full approval process</strong> - creators upload
                    videos for feedback and review
                  </li>
                  <li>
                    • Built-in workflow for content approval before publishing
                  </li>
                  <li>
                    • Perfect for product launches and controlled messaging
                  </li>
                </ul>
              </div>

              <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="font-semibold text-green-800 dark:text-green-300 mb-2">
                  Challenge Campaign:
                </p>
                <ul className="space-y-1 text-green-700 dark:text-green-400">
                  <li>
                    • Open competition format with pre-defined CPM rewards
                  </li>
                  <li>
                    • <strong>AI-verified posts</strong> - automatic validation
                    based on your requirements
                  </li>
                  <li>
                    • Creators get paid based on actual views and performance
                  </li>
                  <li>
                    • Perfect for viral marketing and user-generated content
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Video Approval Workflow
              </Label>
              <p className="text-xs text-muted-foreground">
                Configure how videos are reviewed and approved. You can enable
                multiple review stages.
              </p>
            </div>

            {/* Allow Direct Posting */}
            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="space-y-1 flex-1">
                <Label
                  htmlFor="allowDirectPosting"
                  className="text-sm font-medium text-green-800 dark:text-green-300"
                >
                  Allow Direct Social Media Posting
                </Label>
                <p className="text-xs text-green-700 dark:text-green-400">
                  Creators can upload videos directly to social media without
                  going through the video submission & feedback process.
                  <span className="block mt-1 font-medium">
                    ⚠️ Not recommended if using AI or Manual Review — rejected
                    videos would need to be deleted and reposted, which creators
                    typically dislike.
                  </span>
                </p>
              </div>
              <Switch
                id="allowDirectPosting"
                checked={formData.allowCreatorsToPostDirectlyToSocials}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    allowCreatorsToPostDirectlyToSocials: checked,
                  })
                }
              />
            </div>

            {/* AI Review */}
            <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="space-y-1 flex-1">
                <Label
                  htmlFor="aiReview"
                  className="text-sm font-medium text-blue-800 dark:text-blue-300"
                >
                  🤖 AI Review Required
                </Label>
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  Creators must submit videos for AI review before posting.
                  Videos are automatically checked against your campaign
                  guidelines before proceeding to other review stages.
                </p>
              </div>
              <Switch
                id="aiReview"
                checked={formData.aiReviewRequired}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    aiReviewRequired: checked,
                  })
                }
              />
            </div>
            {/* SuperAdmin Review (uses team review toggle) - Only visible to team members */}
            {isTeamMember && (
              <div className="flex items-center justify-between p-4 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <div className="space-y-1 flex-1">
                  <Label
                    htmlFor="teamReview"
                    className="text-sm font-medium text-indigo-800 dark:text-indigo-300"
                  >
                    👥 SuperAdmin Review Required
                  </Label>
                  <p className="text-xs text-indigo-700 dark:text-indigo-400">
                    SuperAdmin will review all videos as part of the approval
                    workflow.
                  </p>
                </div>
                <Switch
                  id="teamReview"
                  checked={formData.teamVideoReviewRequired}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      teamVideoReviewRequired: checked,
                    })
                  }
                />
              </div>
            )}

            {/* <brand> Team Review (uses manual review toggle) */}
            <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="space-y-1 flex-1">
                <Label
                  htmlFor="manualVideoReview"
                  className="text-sm font-medium text-purple-800 dark:text-purple-300"
                >
                  {brandName} Team Review Required
                </Label>
                <p className="text-xs text-purple-700 dark:text-purple-400">
                  {brandName} Team will review and provide feedback on videos
                  before they go live.
                </p>
              </div>
              <Switch
                id="manualVideoReview"
                checked={formData.manualVideoReviewRequired}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    manualVideoReviewRequired: checked,
                  })
                }
              />
            </div>

            {/* Info box about the workflow - only show ONE message */}
            {formData.teamVideoReviewRequired &&
            formData.manualVideoReviewRequired &&
            formData.aiReviewRequired &&
            formData.allowCreatorsToPostDirectlyToSocials ? (
              <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-indigo-700 dark:text-indigo-300">
                  <p className="font-medium mb-1">
                    Team + AI + Manual Review + Direct Posting:
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators post videos directly to social media, then
                      submit them for review
                    </li>
                    <li>• Step 1: AI reviews submissions against guidelines</li>
                    <li>
                      • Step 2: CreatorGrid team reviews AI-approved videos
                    </li>
                    <li>
                      • Step 3: You manually review and provide feedback on
                      team-approved videos already live on social media
                    </li>
                    <li>
                      • ⚠️ If rejected at any stage, creators must delete the
                      video from social media and repost after making changes
                    </li>
                    <li>
                      • Only fully approved videos are tracked for earnings
                    </li>
                  </ul>
                </div>
              </div>
            ) : formData.teamVideoReviewRequired &&
              formData.manualVideoReviewRequired &&
              formData.allowCreatorsToPostDirectlyToSocials ? (
              <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-indigo-700 dark:text-indigo-300">
                  <p className="font-medium mb-1">
                    Team + Manual Review + Direct Posting:
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators post videos directly to social media, then
                      submit them for review
                    </li>
                    <li>• Step 1: CreatorGrid team reviews submissions</li>
                    <li>
                      • Step 2: You manually review and provide feedback on
                      team-approved videos already live on social media
                    </li>
                    <li>
                      • ⚠️ If rejected at any stage, creators must delete the
                      video from social media and repost after making changes
                    </li>
                    <li>• Only approved videos are tracked for earnings</li>
                  </ul>
                </div>
              </div>
            ) : formData.teamVideoReviewRequired &&
              formData.aiReviewRequired &&
              formData.allowCreatorsToPostDirectlyToSocials ? (
              <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-indigo-700 dark:text-indigo-300">
                  <p className="font-medium mb-1">
                    Team + AI Review + Direct Posting:
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators post videos directly to social media, then
                      submit them for review
                    </li>
                    <li>
                      • Step 1: AI automatically checks submissions against your
                      campaign guidelines
                    </li>
                    <li>
                      • Step 2: CreatorGrid team reviews AI-approved videos
                    </li>
                    <li>
                      • ⚠️ If rejected at any stage, creators must delete the
                      video from social media and repost after making changes
                    </li>
                    <li>• Only approved videos are tracked for earnings</li>
                  </ul>
                </div>
              </div>
            ) : formData.teamVideoReviewRequired &&
              formData.manualVideoReviewRequired &&
              formData.aiReviewRequired ? (
              <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-indigo-700 dark:text-indigo-300">
                  <p className="font-medium mb-1">
                    Team + AI + Manual Review Process:
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators must submit videos for review before posting to
                      social media
                    </li>
                    <li>
                      • Step 1: AI automatically reviews against guidelines
                    </li>
                    <li>
                      • Step 2: CreatorGrid team reviews AI-approved videos
                    </li>
                    <li>
                      • Step 3: You manually review and approve all
                      team-approved videos
                    </li>
                    <li>
                      • Only videos that pass all three reviews can be posted
                      and tracked for earnings
                    </li>
                    <li>
                      • Combines automated efficiency with team and human
                      oversight for maximum quality control
                    </li>
                  </ul>
                </div>
              </div>
            ) : formData.teamVideoReviewRequired &&
              formData.manualVideoReviewRequired ? (
              <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-indigo-700 dark:text-indigo-300">
                  <p className="font-medium mb-1">
                    Team + Manual Review Process:
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators must submit videos for review before posting to
                      social media
                    </li>
                    <li>• Step 1: CreatorGrid team reviews all submissions</li>
                    <li>
                      • Step 2: You manually review and approve all
                      team-approved videos
                    </li>
                    <li>
                      • Only videos that pass both reviews can be posted and
                      tracked for earnings
                    </li>
                    <li>
                      • Combines team oversight with human control for quality
                      assurance
                    </li>
                  </ul>
                </div>
              </div>
            ) : formData.teamVideoReviewRequired &&
              formData.aiReviewRequired ? (
              <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-indigo-700 dark:text-indigo-300">
                  <p className="font-medium mb-1">Team + AI Review Process:</p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators must submit videos for review before posting to
                      social media
                    </li>
                    <li>
                      • Step 1: AI automatically reviews against guidelines
                    </li>
                    <li>
                      • Step 2: CreatorGrid team reviews AI-approved videos
                    </li>
                    <li>
                      • Only videos that pass both reviews can be posted and
                      tracked for earnings
                    </li>
                    <li>
                      • Combines automated efficiency with team oversight for
                      quality control
                    </li>
                  </ul>
                </div>
              </div>
            ) : formData.teamVideoReviewRequired ? (
              <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-indigo-700 dark:text-indigo-300">
                  <p className="font-medium mb-1">Team Review Process:</p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators must submit videos for CreatorGrid team
                      approval before posting to social media
                    </li>
                    <li>
                      • CreatorGrid team reviews and provides feedback on all
                      submissions
                    </li>
                    <li>
                      • Only team-approved videos can be posted and tracked for
                      earnings
                    </li>
                    <li>
                      • Perfect for maintaining high quality standards with team
                      oversight
                    </li>
                  </ul>
                </div>
              </div>
            ) : formData.manualVideoReviewRequired &&
              formData.aiReviewRequired &&
              formData.allowCreatorsToPostDirectlyToSocials ? (
              <div className="flex items-start space-x-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700 dark:text-amber-300">
                  <p className="font-medium mb-1">
                    AI + Manual Review + Direct Posting:
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators post videos directly to social media, then
                      submit them for review
                    </li>
                    <li>• Step 1: AI reviews submissions against guidelines</li>
                    <li>
                      • Step 2: You manually review and provide feedback on
                      videos already live on social media
                    </li>
                    <li>
                      • ⚠️ If rejected at any stage, creators must delete the
                      video from social media and repost after making changes
                    </li>
                    <li>
                      • Only fully approved videos are tracked for earnings
                    </li>
                  </ul>
                </div>
              </div>
            ) : formData.manualVideoReviewRequired &&
              formData.allowCreatorsToPostDirectlyToSocials ? (
              <div className="flex items-start space-x-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700 dark:text-amber-300">
                  <p className="font-medium mb-1">
                    Manual Review + Direct Posting:
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators post videos directly to social media, then
                      submit them for your review
                    </li>
                    <li>
                      • You review and provide feedback on videos already live
                      on social media
                    </li>
                    <li>
                      • ⚠️ If rejected, creators must delete the video from
                      social media and repost after making changes
                    </li>
                    <li>• Only approved videos are tracked for earnings</li>
                  </ul>
                </div>
              </div>
            ) : formData.aiReviewRequired &&
              formData.allowCreatorsToPostDirectlyToSocials ? (
              <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-1">
                    AI Review + Direct Posting:
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators post videos directly to social media, then
                      submit them for AI review
                    </li>
                    <li>
                      • AI automatically checks submissions against your
                      campaign guidelines
                    </li>
                    <li>
                      • ⚠️ If rejected, creators must delete the video from
                      social media and repost after making changes
                    </li>
                    <li>• Only approved videos are tracked for earnings</li>
                  </ul>
                </div>
              </div>
            ) : formData.manualVideoReviewRequired &&
              formData.aiReviewRequired ? (
              <div className="flex items-start space-x-2 p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg">
                <Info className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-indigo-700 dark:text-indigo-300">
                  <p className="font-medium mb-1">
                    AI + Manual Review Process:
                  </p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators must submit videos for review before posting to
                      social media
                    </li>
                    <li>
                      • Step 1: AI automatically reviews against guidelines
                    </li>
                    <li>
                      • Step 2: You manually review and approve all AI-approved
                      videos
                    </li>
                    <li>
                      • Only videos that pass both reviews can be posted and
                      tracked for earnings
                    </li>
                    <li>
                      • Combines automated efficiency with human oversight for
                      maximum quality control
                    </li>
                  </ul>
                </div>
              </div>
            ) : formData.manualVideoReviewRequired ? (
              <div className="flex items-start space-x-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
                <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700 dark:text-amber-300">
                  <p className="font-medium mb-1">Manual Review Process:</p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators must submit videos for your approval before
                      posting to social media
                    </li>
                    <li>
                      • You review and provide feedback on all submissions
                    </li>
                    <li>
                      • Only approved videos can be posted and tracked for
                      earnings
                    </li>
                    <li>
                      • Perfect for maintaining brand quality and messaging
                    </li>
                  </ul>
                </div>
              </div>
            ) : formData.aiReviewRequired ? (
              <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-1">AI Review Process:</p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators must submit videos for AI review before posting
                      to social media
                    </li>
                    <li>
                      • AI automatically checks submissions against your
                      campaign guidelines
                    </li>
                    <li>
                      • Provides automated feedback on content quality and
                      compliance
                    </li>
                    <li>
                      • Speeds up the review process while maintaining quality
                      standards
                    </li>
                  </ul>
                </div>
              </div>
            ) : formData.allowCreatorsToPostDirectlyToSocials ? (
              <div className="flex items-start space-x-2 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                <Info className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-green-700 dark:text-green-300">
                  <p className="font-medium mb-1">Direct Posting Enabled:</p>
                  <ul className="space-y-0.5">
                    <li>
                      • Creators can upload videos directly to social media
                      without submitting for review
                    </li>
                    <li>• No review or approval process required</li>
                    <li>
                      • Videos are automatically tracked via social media
                      platform APIs after posting
                    </li>
                    <li>
                      • Best for trusted partnerships and streamlined workflows
                    </li>
                  </ul>
                </div>
              </div>
            ) : null}
          </div>

          {/* Campaign Budget */}
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="budget" className="text-sm font-medium">
                Campaign Budget (Optional)
              </Label>
              <p className="text-xs text-muted-foreground">
                Set a total budget cap for your campaign. Once approved video
                earnings reach this amount, creators will no longer be able to
                post new content and video views will be frozen.
              </p>
            </div>
            <div className="space-y-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-primary/60">
                  $
                </span>
                <Input
                  id="budget"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.budget || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      budget: e.target.value
                        ? parseFloat(e.target.value)
                        : undefined,
                    })
                  }
                  placeholder="1000.00"
                  className="w-full pl-8"
                />
              </div>
            </div>
            {/* Budget Estimation */}
            {formData.budget &&
              formData.budget > 0 &&
              (() => {
                // Check if we have specific CPM values set
                const hasPredefinedCpm =
                  formData.predefinedCpm && formData.predefinedCpm > 0;
                const hasCpmRange =
                  (formData.budgetCpmMin && formData.budgetCpmMin > 0) ||
                  (formData.budgetCpmMax && formData.budgetCpmMax > 0);

                return (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h5 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-3">
                      Budget Estimation - Views Coverage
                    </h5>

                    {hasPredefinedCpm ? (
                      // Show estimation based on predefined CPM (challenge campaigns)
                      <div>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                          With a ${formData.budget.toLocaleString()} budget at $
                          {formData.predefinedCpm} CPM:
                        </p>
                        <div className="p-3 bg-white dark:bg-gray-800 rounded border">
                          <div className="text-lg font-semibold text-blue-800 dark:text-blue-300">
                            ~
                            {Math.floor(
                              ((formData.budget || 0) * 1000) /
                                (formData.predefinedCpm || 1),
                            ).toLocaleString()}{" "}
                            total views
                          </div>
                          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            This is how many views your entire budget will cover
                            across all creators
                          </div>
                        </div>
                      </div>
                    ) : hasCpmRange ? (
                      // Show estimation based on CPM range
                      <div>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                          With a ${formData.budget.toLocaleString()} budget
                          using your CPM range:
                        </p>
                        <div className="grid grid-cols-1 gap-2">
                          {formData.budgetCpmMin &&
                            formData.budgetCpmMin > 0 && (
                              <div className="p-2 bg-white dark:bg-gray-800 rounded border">
                                <div className="font-medium text-blue-800 dark:text-blue-300">
                                  At Min CPM (${formData.budgetCpmMin})
                                </div>
                                <div className="text-blue-600 dark:text-blue-400">
                                  ~
                                  {Math.floor(
                                    ((formData.budget || 0) * 1000) /
                                      formData.budgetCpmMin,
                                  ).toLocaleString()}{" "}
                                  views
                                </div>
                              </div>
                            )}
                          {formData.budgetCpmMax &&
                            formData.budgetCpmMax > 0 && (
                              <div className="p-2 bg-white dark:bg-gray-800 rounded border">
                                <div className="font-medium text-blue-800 dark:text-blue-300">
                                  At Max CPM (${formData.budgetCpmMax})
                                </div>
                                <div className="text-blue-600 dark:text-blue-400">
                                  ~
                                  {Math.floor(
                                    ((formData.budget || 0) * 1000) /
                                      formData.budgetCpmMax,
                                  ).toLocaleString()}{" "}
                                  views
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    ) : (
                      // Show multiple CPM scenarios when no specific CPM is set
                      <div>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mb-3">
                          With a ${formData.budget.toLocaleString()} budget,
                          here's approximately how many total views you could
                          get:
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { cpm: 1.0, label: "Low CPM ($1.00)" },
                            { cpm: 3.0, label: "Average CPM ($3.00)" },
                            { cpm: 5.0, label: "Good CPM ($5.00)" },
                            { cpm: 10.0, label: "High CPM ($10.00)" },
                          ].map(({ cpm, label }) => {
                            const totalViews = Math.floor(
                              ((formData.budget || 0) * 1000) / cpm,
                            );
                            return (
                              <div
                                key={cpm}
                                className="p-2 bg-white dark:bg-gray-800 rounded border text-xs"
                              >
                                <div className="font-medium text-blue-800 dark:text-blue-300">
                                  {label}
                                </div>
                                <div className="text-blue-600 dark:text-blue-400">
                                  ~{totalViews.toLocaleString()} views
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                          💡 Higher CPM = fewer total views but potentially
                          better targeting/quality
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}

            <div className="flex items-start space-x-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg">
              <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-700 dark:text-amber-300">
                <p className="font-medium mb-1">How Budget Works:</p>
                <ul className="space-y-0.5">
                  <li>• Creators can see your total campaign budget</li>
                  <li>
                    • Each approved video's earnings count toward this budget
                  </li>
                  <li>
                    • When earnings reach the budget limit, new posts are
                    disabled
                  </li>
                  <li>• Video views are frozen to prevent further earnings</li>
                  <li>• Leave empty for unlimited campaign budget</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Challenge-specific fields */}
          {campaignType === "challenge" && (
            <>
              {/* CPM Reward */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    CPM Reward (Cost Per 1,000 Views) *
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Set the CPM reward creators will earn for every 1,000 views
                    on their content.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.predefinedCpm || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        predefinedCpm: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    placeholder="2.00"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCpmCalculator(!showCpmCalculator)}
                    className="whitespace-nowrap"
                  >
                    Calculator
                  </Button>
                </div>

                {showCpmCalculator && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-blue-800 dark:text-blue-300">
                        CPM Calculator
                      </h4>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          </TooltipTrigger>
                          <TooltipContent
                            side="right"
                            className="max-w-[400px]"
                          >
                            Formula: 1000 × (Views-to-download %) ×
                            (Download-to-paid %) × (LTV × CAC:LTV ratio)
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label className="text-xs text-blue-700 dark:text-blue-400">
                          Lifetime Revenue per User ($)
                        </Label>
                        <Input
                          type="number"
                          value={cpmCalculator.lifetimeRevenue}
                          onChange={(e) =>
                            setCpmCalculator({
                              ...cpmCalculator,
                              lifetimeRevenue: e.target.value,
                            })
                          }
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-blue-700 dark:text-blue-400">
                          CAC:LTV Ratio
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={cpmCalculator.cacLtvRatio}
                          onChange={(e) =>
                            setCpmCalculator({
                              ...cpmCalculator,
                              cacLtvRatio: e.target.value,
                            })
                          }
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-blue-700 dark:text-blue-400">
                          Views-to-download conversion (%)
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={cpmCalculator.viewsToDownload}
                          onChange={(e) =>
                            setCpmCalculator({
                              ...cpmCalculator,
                              viewsToDownload: e.target.value,
                            })
                          }
                          className="mt-1"
                          placeholder="0.25"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-blue-700 dark:text-blue-400">
                          Download-to-paid conversion (%)
                        </Label>
                        <Input
                          type="number"
                          value={cpmCalculator.downloadToPaid}
                          onChange={(e) =>
                            setCpmCalculator({
                              ...cpmCalculator,
                              downloadToPaid: e.target.value,
                            })
                          }
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-white dark:bg-gray-800 rounded border">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                        Recommended CPM: $
                        {isNaN(calculateRecommendedCpm())
                          ? "0.00"
                          : calculateRecommendedCpm().toFixed(2)}
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            predefinedCpm: parseFloat(
                              calculateRecommendedCpm().toFixed(2),
                            ),
                          })
                        }
                        className="mt-2"
                        disabled={isNaN(calculateRecommendedCpm())}
                      >
                        Use This CPM
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-700 dark:text-blue-300">
                    <p className="font-medium mb-1">Challenge CPM Example:</p>
                    <p>
                      A $2 CPM means creators earn $2 for every 1,000 views. If
                      their video gets 50,000 views, they earn $100 ($2 × 50).
                    </p>
                  </div>
                </div>
              </div>

              {/* Validation Requirements */}
              <div className="space-y-2">
                <Label
                  htmlFor="validationRequirements"
                  className="text-sm font-medium"
                >
                  Post Validation Requirements *
                </Label>
                <RichTextEditor
                  value={formData.validationRequirements || ""}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      validationRequirements: value,
                    })
                  }
                  placeholder="e.g., At least one app screenshot with our company logo visible in it"
                  className="min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">
                  Specify what makes a post valid for your challenge. Our AI
                  will automatically verify each post against these
                  requirements. Creators can challenge the AI decision for
                  manual review.
                </p>
              </div>
            </>
          )}

          {/* Creator Brief */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="creatorBrief" className="text-sm font-medium">
                Creator Brief (Optional)
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setFormData({
                    ...formData,
                    creatorBrief: getTemplateBrief(campaignType),
                  })
                }
                className="text-xs"
              >
                Use Template
              </Button>
            </div>
            <RichTextEditor
              value={formData.creatorBrief || ""}
              onChange={(value) =>
                setFormData({ ...formData, creatorBrief: value })
              }
              placeholder="Describe what you're looking for in creators and content..."
              className="min-h-[150px] font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Provide detailed guidelines for creators about content goals,
              style, requirements, and expectations. Use the template button for
              a comprehensive starting point.
            </p>
          </div>

          {/* What Problems Does Your Product Solve */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="problemsSolved" className="text-sm font-medium">
                What Problems Does Your Product Solve (Optional)
              </Label>
              {isAutoSaving && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Auto-saving...
                </div>
              )}
            </div>
            <RichTextEditor
              value={formData.problemsSolved || ""}
              onChange={(value) =>
                setFormData({ ...formData, problemsSolved: value })
              }
              placeholder="Describe the key problems your product solves for users..."
              className="min-h-[100px]"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Help creators understand the value proposition of your product
                and what pain points it addresses for users.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setAiScrapingDialogType("usps");
                  setAiScrapingDialogOpen(true);
                }}
                disabled={isAiScraping}
                className="text-xs"
              >
                {isAiScraping ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  "Let AI help"
                )}
              </Button>
            </div>
          </div>

          {/* Customer Testimonials */}
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  Customer Testimonials (Optional)
                </Label>
                {isAutoSaving && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Auto-saving...
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Add customer testimonials to provide social proof. Each
                testimonial should include the customer's feedback and their
                name.
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addTestimonial}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Testimonial
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setAiScrapingDialogType("testimonials");
                  setAiScrapingDialogOpen(true);
                }}
                disabled={isAiScraping}
                className="text-xs"
              >
                {isAiScraping ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  "Let AI help"
                )}
              </Button>
            </div>

            {formData.customerTestimonials &&
              formData.customerTestimonials.length > 0 && (
                <div className="space-y-3">
                  {formData.customerTestimonials.map((testimonial, index) => (
                    <div
                      key={index}
                      className="p-4 border rounded-lg space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">
                          Testimonial {index + 1}
                        </Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeTestimonial(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                          Customer Feedback *
                        </Label>
                        <RichTextEditor
                          value={testimonial.testimonial}
                          onChange={(value) =>
                            updateTestimonial(index, "testimonial", value)
                          }
                          placeholder="e.g., 'This app completely transformed how I manage my daily tasks. I can't imagine working without it!'"
                          className="w-full min-h-[80px]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                          Customer Name *
                        </Label>
                        <Input
                          type="text"
                          value={testimonial.customerName}
                          onChange={(e) =>
                            updateTestimonial(
                              index,
                              "customerName",
                              e.target.value,
                            )
                          }
                          placeholder="e.g., Sarah Johnson, Marketing Manager"
                          className="w-full"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

            <div className="flex items-start space-x-2 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
              <Info className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-green-700 dark:text-green-300">
                <p className="font-medium mb-1">Testimonial Tips:</p>
                <ul className="space-y-0.5">
                  <li>
                    • Include specific benefits or results customers experienced
                  </li>
                  <li>• Use authentic, conversational language</li>
                  <li>• Include customer's name and role/title if possible</li>
                  <li>• Keep testimonials concise but impactful</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Creator Criteria - Only show when applications are required */}
          {requiresApplications() && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Creator Evaluation Criteria
                </Label>
                <p className="text-xs text-muted-foreground">
                  Define specific criteria to evaluate creator applications.
                  These will be used by AI to score and rank applicants.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addCriterion}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Criterion
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={useTemplateCriteria}
                >
                  Use Template
                </Button>
              </div>

              {formData.creatorCriteria &&
                formData.creatorCriteria.length > 0 && (
                  <div className="space-y-3">
                    {formData.creatorCriteria.map((criterion, index) => (
                      <div
                        key={index}
                        className="p-4 border rounded-lg space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium">
                            Criterion {index + 1}
                          </Label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCriterion(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">
                            Title *
                          </Label>
                          <Input
                            type="text"
                            value={criterion.title}
                            onChange={(e) =>
                              updateCriterion(index, "title", e.target.value)
                            }
                            placeholder="e.g., Content Quality & Production Value"
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">
                            Description *
                          </Label>
                          <Textarea
                            value={criterion.description}
                            onChange={(e) =>
                              updateCriterion(
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            placeholder="Describe what this criterion evaluates..."
                            className="w-full min-h-[80px]"
                            rows={3}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">
                            Weight (Optional)
                          </Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={criterion.weight || ""}
                            onChange={(e) =>
                              updateCriterion(
                                index,
                                "weight",
                                e.target.value ? parseInt(e.target.value) : "",
                              )
                            }
                            placeholder="0-100"
                            className="w-full"
                          />
                          <p className="text-xs text-muted-foreground">
                            Percentage weight for this criterion (leave empty
                            for equal weighting)
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-1">How it works:</p>
                  <p>
                    AI will evaluate each creator application against these
                    criteria and provide scores. This helps you identify the
                    best candidates for your campaign efficiently.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Campaign Visibility */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Campaign Visibility</Label>
            {campaignType === "challenge" ? (
              <Select
                value={
                  formData.isPublic
                    ? formData.applicationNotRequired
                      ? "public"
                      : "application-only"
                    : "private"
                }
                onValueChange={(value: string) => {
                  if (value === "public") {
                    setFormData({
                      ...formData,
                      isPublic: true,
                      applicationNotRequired: true,
                    });
                  } else if (value === "application-only") {
                    setFormData({
                      ...formData,
                      isPublic: true,
                      applicationNotRequired: false,
                    });
                  } else {
                    setFormData({
                      ...formData,
                      isPublic: false,
                      applicationNotRequired: false,
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public (Open to All)</SelectItem>
                  <SelectItem value="application-only">
                    Application Only
                  </SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Select
                value={formData.isPublic ? "public" : "private"}
                onValueChange={(value: string) =>
                  setFormData({ ...formData, isPublic: value === "public" })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div className="space-y-1 text-xs text-muted-foreground">
              {campaignType === "challenge" ? (
                <>
                  <p>
                    <strong>Public (Open to All):</strong> Anyone can
                    participate immediately without approval
                  </p>
                  <p>
                    <strong>Application Only:</strong> Visible to all, but
                    creators must apply and be approved
                  </p>
                  <p>
                    <strong>Private:</strong> Invitation-only and not visible to
                    creators
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <strong>Public:</strong> Can be discovered and applied to by
                    all creators
                  </p>
                  <p>
                    <strong>Private:</strong> Invitation-only and not visible to
                    creators
                  </p>
                </>
              )}
            </div>
          </div>

          {/* End Date */}
          <div className="space-y-2">
            <Label htmlFor="endDate" className="text-sm font-medium">
              End Date (Optional)
            </Label>
            <Input
              id="endDate"
              type="date"
              value={endDateInput}
              onChange={(e) => setEndDateInput(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
            />
            <p className="text-xs text-muted-foreground">
              Set an end date for your campaign. Leave empty for ongoing
              campaigns.
            </p>
          </div>

          {/* Allowed Channels */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Allowed Platforms</Label>
            <p className="text-xs text-muted-foreground">
              Select which platforms creators can use for this campaign.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {(
                [
                  { key: "instagram", label: "Instagram" },
                  { key: "tiktok", label: "TikTok" },
                  { key: "youtube-shorts", label: "YouTube Shorts" },
                  { key: "youtube", label: "YouTube Long Form" },
                ] as const
              ).map((platform) => (
                <label
                  key={platform.key}
                  className="flex items-center space-x-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={
                      formData.allowedChannels?.includes(platform.key) ?? false
                    }
                    onChange={(e) => {
                      const currentChannels = formData.allowedChannels || [];
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          allowedChannels: [...currentChannels, platform.key],
                        });
                      } else {
                        setFormData({
                          ...formData,
                          allowedChannels: currentChannels.filter(
                            (channel) => channel !== platform.key,
                          ),
                        });
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                  <span>{platform.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Budget Range - Only for non-challenge campaigns */}
          {campaignType !== "challenge" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Budget Range (Optional)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Set budget ranges to help creators understand your investment
                  level. You can specify CPM (per 1,000 views) and/or flat fee
                  ranges.
                </p>
              </div>

              {/* CPM Budget */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  CPM Budget (Cost Per 1,000 Views)
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="budgetCpmMin" className="text-sm">
                      Minimum CPM ($)
                    </Label>
                    <Input
                      id="budgetCpmMin"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.budgetCpmMin || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          budgetCpmMin: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budgetCpmMax" className="text-sm">
                      Maximum CPM ($)
                    </Label>
                    <Input
                      id="budgetCpmMax"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.budgetCpmMax || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          budgetCpmMax: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-700 dark:text-blue-300">
                    <p className="font-medium mb-1">CPM Example:</p>
                    <p>
                      A $2 CPM means you'll pay $2 for every 1,000 views. If
                      their video gets 50,000 views, you'd pay $100 ($2 × 50).
                    </p>
                  </div>
                </div>
              </div>

              {/* Flat Fee Budget */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-green-700 dark:text-green-300">
                  Flat Fee Budget (Per Video/Content)
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="budgetFlatFeeMin" className="text-sm">
                      Minimum Flat Fee ($)
                    </Label>
                    <Input
                      id="budgetFlatFeeMin"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.budgetFlatFeeMin || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          budgetFlatFeeMin: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budgetFlatFeeMax" className="text-sm">
                      Maximum Flat Fee ($)
                    </Label>
                    <Input
                      id="budgetFlatFeeMax"
                      type="number"
                      step="1"
                      min="0"
                      value={formData.budgetFlatFeeMax || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          budgetFlatFeeMax: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="flex items-start space-x-2 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                  <Info className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-green-700 dark:text-green-300">
                    <p className="font-medium mb-1">Flat Fee Example:</p>
                    <p>
                      A $500 flat fee means you'll pay $500 per video regardless
                      of views. Good for guaranteed compensation and budget
                      certainty.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  <strong>Note:</strong> You can set both CPM and flat fee
                  ranges, or just one type. This helps creators understand your
                  budget flexibility and preferred payment structure.
                </p>
              </div>
            </div>
          )}

          {/* Eligible Countries Selection */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Creator Eligibility by Country
              </Label>
              <p className="text-xs text-muted-foreground">
                Select which countries' creators are eligible to participate in
                this campaign. All approved countries are selected by default.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg bg-muted/20">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h5 className="text-sm font-medium text-primary">
                    Eligible Countries (
                    {formData.eligibleCountries?.length || 0} of{" "}
                    {APPROVED_STRIPE_CONNECT_COUNTRIES.length} selected)
                  </h5>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          eligibleCountries: APPROVED_STRIPE_CONNECT_COUNTRIES,
                        })
                      }
                      className="text-xs"
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          eligibleCountries: [],
                        })
                      }
                      className="text-xs"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                  {Object.entries(
                    countries as { [code: string]: { name: string } },
                  )
                    .filter(([code]) =>
                      APPROVED_STRIPE_CONNECT_COUNTRIES.includes(code),
                    )
                    .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                    .map(([code, country]) => (
                      <label
                        key={code}
                        className="flex items-center gap-2 p-2 hover:bg-background/50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData.eligibleCountries?.includes(code) || false
                          }
                          onChange={(e) => {
                            const currentCountries =
                              formData.eligibleCountries || [];
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                eligibleCountries: [...currentCountries, code],
                              });
                            } else {
                              setFormData({
                                ...formData,
                                eligibleCountries: currentCountries.filter(
                                  (c) => c !== code,
                                ),
                              });
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">
                          {getUnicodeFlagIcon(code)}
                        </span>
                        <span className="text-xs font-medium truncate">
                          {country.name}
                        </span>
                      </label>
                    ))}
                </div>

                <div className="flex items-start space-x-2 p-2 bg-blue-50 dark:bg-blue-950/20 rounded">
                  <Info className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Only creators with Stripe Connect accounts from selected
                    countries will be able to apply and participate in this
                    campaign. This ensures proper payment processing.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Campaign"
              )}
            </Button>
          </div>
        </form>

        <AiScrapingDialog
          open={aiScrapingDialogOpen}
          onOpenChange={setAiScrapingDialogOpen}
          type={aiScrapingDialogType}
          onSubmit={handleAiScraping}
          isLoading={isAiScraping}
        />
      </DialogContent>
    </Dialog>
  );
}
